import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Bell,
  Loader2,
  ArrowLeft,
  Megaphone,
  Clock,
  Sparkles,
  User,
  CheckCircle,
  ExternalLink,
  CheckCheck,
  Inbox,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const MESSAGE_TYPE_CONFIG = {
  SYSTEM_ANNOUNCEMENT: {
    label: 'Announcement',
    icon: Megaphone,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    badgeClass: 'bg-blue-100 text-blue-700'
  },
  REMINDER: {
    label: 'Reminder',
    icon: Clock,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    badgeClass: 'bg-amber-100 text-amber-700'
  },
  OPPORTUNITY: {
    label: 'Opportunity',
    icon: Sparkles,
    bgColor: 'bg-lime-50',
    textColor: 'text-lime-600',
    borderColor: 'border-lime-200',
    badgeClass: 'bg-lime-100 text-lime-700'
  },
  PERSONAL: {
    label: 'Personal',
    icon: User,
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    badgeClass: 'bg-purple-100 text-purple-700'
  }
};

const MessageCard = ({ message, onClick, onMarkRead }) => {
  const config = MESSAGE_TYPE_CONFIG[message.message_type] || MESSAGE_TYPE_CONFIG.SYSTEM_ANNOUNCEMENT;
  const Icon = config.icon;
  
  return (
    <div
      className={`p-4 rounded-xl border transition-all cursor-pointer ${
        message.is_read 
          ? 'bg-white border-slate-200 hover:border-slate-300' 
          : `${config.bgColor} ${config.borderColor} hover:shadow-md`
      }`}
      onClick={() => onClick(message)}
    >
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg ${message.is_read ? 'bg-slate-100' : config.bgColor}`}>
          <Icon className={`h-5 w-5 ${message.is_read ? 'text-slate-400' : config.textColor}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge className={message.is_read ? 'bg-slate-100 text-slate-500' : config.badgeClass}>
              {config.label}
            </Badge>
            {!message.is_read && (
              <span className="h-2 w-2 rounded-full bg-lime-500 animate-pulse" />
            )}
            <span className="text-xs text-slate-400 ml-auto">
              {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
            </span>
          </div>
          
          <h3 className={`font-medium ${message.is_read ? 'text-slate-600' : 'text-slate-900'}`}>
            {message.title}
          </h3>
          
          <p className={`text-sm mt-1 line-clamp-2 ${message.is_read ? 'text-slate-400' : 'text-slate-600'}`}>
            {message.body}
          </p>
          
          {message.action_label && (
            <div className="mt-2">
              <span className={`text-sm font-medium ${config.textColor}`}>
                {message.action_label} →
              </span>
            </div>
          )}
        </div>
        
        {!message.is_read && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead(message.id);
            }}
          >
            <CheckCircle className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

const MessageDetailModal = ({ message, isOpen, onClose, onAction }) => {
  if (!message) return null;
  
  const config = MESSAGE_TYPE_CONFIG[message.message_type] || MESSAGE_TYPE_CONFIG.SYSTEM_ANNOUNCEMENT;
  const Icon = config.icon;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${config.bgColor}`}>
              <Icon className={`h-5 w-5 ${config.textColor}`} />
            </div>
            <Badge className={config.badgeClass}>{config.label}</Badge>
          </div>
          <DialogTitle className="text-xl">{message.title}</DialogTitle>
        </DialogHeader>
        
        <div className="mt-4">
          <p className="text-slate-600 whitespace-pre-wrap">{message.body}</p>
          
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-400">
              Sent {format(new Date(message.created_at), 'MMMM d, yyyy \'at\' h:mm a')}
            </p>
          </div>
          
          {message.action_url && message.action_label && (
            <div className="mt-4">
              <Button 
                className="w-full bg-evergreen-950 hover:bg-evergreen-900"
                onClick={() => onAction(message.action_url)}
              >
                {message.action_label}
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function MessageCenterPage() {
  const { authAxios, user, logout } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const [unreadCount, setUnreadCount] = useState(0);

  const loadMessages = useCallback(async () => {
    try {
      const response = await authAxios.get('/candidates/messages', {
        params: { unread_only: filter === 'unread', limit: 50 }
      });
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [authAxios, filter]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleMessageClick = async (message) => {
    setSelectedMessage(message);
    setModalOpen(true);
    
    // Mark as read if unread
    if (!message.is_read) {
      try {
        await authAxios.put(`/candidates/messages/${message.id}/read`);
        setMessages(prev => prev.map(m => 
          m.id === message.id ? { ...m, is_read: true } : m
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await authAxios.put(`/candidates/messages/${messageId}/read`);
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Marked as read');
    } catch (error) {
      console.error('Failed to mark as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await authAxios.put('/candidates/messages/mark-all-read');
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      setUnreadCount(0);
      toast.success('All messages marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleAction = (actionUrl) => {
    setModalOpen(false);
    if (actionUrl.startsWith('http')) {
      window.open(actionUrl, '_blank');
    } else {
      navigate(actionUrl);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading text-slate-900 flex items-center gap-2">
              <Bell className="h-6 w-6 text-lime-600" />
              Message Center
              {unreadCount > 0 && (
                <Badge className="bg-lime-500 text-white ml-2">{unreadCount} new</Badge>
              )}
            </h1>
            <p className="text-slate-500 mt-1">Stay updated with announcements, opportunities, and reminders</p>
          </div>
          
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-evergreen-950' : ''}
          >
            <Inbox className="h-4 w-4 mr-2" />
            All Messages
          </Button>
          <Button
            variant={filter === 'unread' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('unread')}
            className={filter === 'unread' ? 'bg-evergreen-950' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Unread Only
            {unreadCount > 0 && (
              <Badge className="ml-2 bg-white/20 text-white">{unreadCount}</Badge>
            )}
          </Button>
        </div>

        {/* Messages List */}
        {messages.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {filter === 'unread' ? 'No unread messages' : 'No messages yet'}
              </h3>
              <p className="text-slate-500">
                {filter === 'unread' 
                  ? 'You\'re all caught up! Switch to "All Messages" to see your history.'
                  : 'When you receive messages, they\'ll appear here.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {messages.map(message => (
              <MessageCard
                key={message.id}
                message={message}
                onClick={handleMessageClick}
                onMarkRead={handleMarkRead}
              />
            ))}
          </div>
        )}
      </main>

      {/* Message Detail Modal */}
      <MessageDetailModal
        message={selectedMessage}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onAction={handleAction}
      />
    </div>
  );
}
